import omit from 'lodash-es/omit';

import { isNullOrUndefined } from '@app/util/util';
import { BookingArrangementVM, Travelers, AutoBookingPayload } from '@booking/entities/booking-arrangement';
import { BillingResponse, BillingPayload } from '@billing/entities/billing.payload';
import { createAt, payloadToCreate } from '@billing/mappers/billing-id.mapper';
import { BOOKING_FORM_INPUTS, TRAVELER_FORM_INPUTS } from '@booking/constants/forms.constants';
import { formatTravelerDate } from '@booking/mappers/traveler-date.mapper';
import { VehicleDriver } from '@autos/entities/vehicle-booking.payload';
import { AssistanceBookPayload } from '@shared/travel-assistance/entities/booking-assistance';
import { assistanceBookPayload } from '@booking/mappers/assistance-search-payload.mapper';
import { dobToDate } from '@ux/mappers/date.mapper';

export function autoBookingPayloadMapper(
  ValidationId: string,
  arrangement: BookingArrangementVM,
  billingInfo: BillingResponse,
  assistanceInfo: { optionId: number, searchId: string }
): AutoBookingPayload {
  const travelers = getTravelers(arrangement);
  const createIsEnabledByBilling = createAt(billingInfo);
  const createIsEnabledByAssistance = !isNullOrUndefined(assistanceInfo?.optionId);
  return {
    arrangement: {
      ValidateId: ValidationId,
      TravelItineraryId: 0,
      AdditionalInformation: arrangement.Comments,
      Driver: mapTravelerToDriver(travelers[0])
    },
    billing: {
      createIsEnabled: createIsEnabledByBilling,
      payload: createIsEnabledByBilling ? payloadToCreate(billingInfo, arrangement) : {} as BillingPayload
    },
    assistance: {
      createIsEnabled: createIsEnabledByAssistance,
      payload: createIsEnabledByAssistance ?
        assistanceBookPayload(assistanceInfo?.searchId, travelers, assistanceInfo?.optionId) : {} as AssistanceBookPayload
    },
  };
}

function mapTravelerToDriver(traveler: Travelers): VehicleDriver {
  return {
    Gender: traveler?.Gender,
    FirstName: traveler?.FirstName,
    LastName: traveler?.LastName,
    Phone: traveler?.Phone,
    Email: traveler?.Email,
    DocumentNumber: traveler?.DocumentNumber,
    ConfigurationDocumentId: traveler?.ConfigurationDocumentId,
    Nationality: traveler?.Nationality,
    DOB: traveler?.DOB
  };
}

function formatTraveler(traveler: Travelers): Travelers {
  const travelerName = `${traveler[TRAVELER_FORM_INPUTS.FIRST_NAME]} ${traveler[TRAVELER_FORM_INPUTS.SECOND_NAME]}`.trim();
  return omit({ ...traveler, [TRAVELER_FORM_INPUTS.FIRST_NAME]: travelerName },
    [
      TRAVELER_FORM_INPUTS.SECOND_NAME,
      TRAVELER_FORM_INPUTS.USER_ID,
      TRAVELER_FORM_INPUTS.INTERNATIONAL_DOCUMENT_REQUIRED
    ]);
}

function getTravelers(arrangement: BookingArrangementVM): Travelers[] {
  return arrangement[BOOKING_FORM_INPUTS.TRAVELERS].map(traveler => ({
    ...formatTraveler(traveler),
    DOB: formatTravelerDate(dobToDate(traveler.DOB)),
    Phone: arrangement.Phone,
    Email: arrangement.Email
  }));
}
